import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box, Fade } from '@mui/material';

import { Authenticator, CheckboxField, Flex, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { I18n, Auth } from 'aws-amplify';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ampli } from '../index.js';
// import { LaunchPreviewFeatureTable } from '../Subscription/sku_launch/FeatureTable';
import { red, gray, brand } from '../getTheme/getTheme';

import nycExposureWebp from '../static/nyc_exposure_stop_0.25_q70.webp';
// import HeptagonsSVG from '../static/pattern_heptagons.svg';

import { AmplifyWrapper } from './AmplifyWrapper';

I18n.putVocabulariesForLanguage('en', {
    'Create Account': 'Start Free Trial',
    'Given Name': 'First Name',
    'Enter your Given Name': 'Enter your first name',
    'Family Name': 'Last Name',
    'Enter your Family Name': 'Enter your last name',
    'We Texted You': 'We E-mailed You',
    'Enter your Email': 'E-mail',
    'Enter your Password': 'Password',
    "PreSignUp failed with error Invalid Invitation Code.": "The invitation code you entered is invalid. Please check the code and try again. If you don't have a valid code, please join our waitlist with the code WAITLIST during signup.",
    "PreAuthentication failed with error You are still on a wait list!.": "Thanks for your interest! We've placed you on the deepKPI waitlist and will be in touch soon.",
    "PreAuthentication failed with error 'email'.": "We couldn't find an account with those credentials. Please try again.",
    "PostConfirmation failed with error Sorry you are on the waitlist!.": "Thanks for your interest! We've placed you on the deepKPI waitlist and will be in touch soon.",
});


const PERMITTED_DEST_URLS = [
    process.env.REACT_APP_WWW_URL,
    process.env.REACT_APP_DEEPKPI_URL,
    process.env.REACT_APP_DEEPANALYSIS_URL,
    process.env.REACT_APP_DEEPINSIGHT_URL,
]

function permittedDst(queryStringFrom) {
    const url = new URL(queryStringFrom).origin;
    return PERMITTED_DEST_URLS.includes(url);
}

export function Logout() {
    const navigate = useNavigate();

    useEffect(() => {
        const performLogout = async () => {
            try {
                // Check if user is authenticated using Auth directly
                const currentUser = await Auth.currentAuthenticatedUser();
                if (currentUser) {
                    console.log("Sign out ", currentUser.username);
                    
                    // Sign out using Auth directly
                    await Auth.signOut();
                    
                    // Flush amplitude events
                    ampli.flush();
                    if (ampli.client) {
                        ampli.client.reset();
                    }
                }
            } catch (error) {
                console.log("User not authenticated or error during logout:", error.message);
            } finally {
                // Always redirect to login
                navigate('/login', { replace: true });
            }
        };

        performLogout();
    }, [navigate]);

    return null;
}





export function Login({ tab, errorInfo }) {
    const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user]);  // PUBKPI-1229 -- removed updateUser, which is a hallucinated API.  See supported access contexts here: https://ui.docs.amplify.aws/react/connected-components/authenticator/advanced#full-api
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [error, setError] = useState('');
    const [invitationCode, setInvitationCode] = useState('');
    
    useEffect(() => {
        // OAuth errors get passed into the /dashboard route as query parameters. We'll
        // ask it to parse them and send them in here, where we have to pick them up 
        // and handle them.
        if (errorInfo?.error && errorInfo?.errorDescription) {
            console.log("OAuth error picked up in Login:", errorInfo);
            const decodedError = decodeURIComponent(errorInfo.errorDescription);
            if (decodedError.includes('UsernameExistsException')) {
                setError('A Revelata account already exists for that e-mail address.\nPlease try to log in by entering your e-mail and password.');
            }
        }
    }, [errorInfo]);
    
    useEffect(() => {
        // Get invitation code from URL if present
        const urlInvitationCode = searchParams.get("invitation_code");
        if (urlInvitationCode) {
            setInvitationCode(urlInvitationCode);
            document.cookie = `invitation_code=${urlInvitationCode}; path=/; max-age=86400`; // Store for 24 hours
        }
    }, [searchParams]);

    /*  
    
    Note that we cannot currently fire the ampli.commonLoginInitiated() or
    ampli.commonCreateAccountInitiated() events in this component because we cannot access the tab
    state of the Authenticator component.  i.e., when the user clicks on the "Sign Up" or "Create
    Account" tabs, we can't detect that.  So for now, we can only fire these events from the
    TopAppBar/landing page button clicks. 
    
    */

    
    // If authenticated, then forward to appropriate location.  Location specified in querystring
    // parameter takes precedence over location.state.from, but only if it has an allowed host.
    let queryStringFrom = searchParams.get("from");    
    let navigateViaRoute = !queryStringFrom || !permittedDst(queryStringFrom);
    let from = location.state?.from?.pathname || process.env.REACT_APP_LOGGED_IN_REDIRECT_DEST;
    
    useEffect(() => {
        const handleAuth = async () => {
            if (authStatus === 'authenticated') {
                if (process.env.NODE_ENV === "development") { 
                    console.log("Sign in user object:", user);
                    console.log("Sign in attributes:", user?.attributes);
                    console.log("Sign in route:", authStatus);
                };                
                                
                if (navigateViaRoute) {
                    // We need to keep this redirect same domain to not trip security
                    // policy violations when Google Auth redirects back to us. For now
                    // we're going to ask www to implement a dashboard route, which at
                    // today is a redirect to pubkpi :(

                    // Note that the dashboard will log users into Amplitude; we'll only do 
                    // that manually if we're not going to the dashboard.
                    navigate('/dashboard');
                } else {
                    // We have to await because we are changing window.location.href.  If we don't await, the
                    // change can cancel the async calls to Amplitude.            
                    await Promise.all([
                        ampli.identify(user.signInUserSession.idToken.payload.sub),
                        ampli.login(),
                        ampli.commonLoginCompleted()
                    ]);
                    
                    await ampli.flush();  // Must be done otherwise batched events will not be sent.

                    window.location.assign(new URL(queryStringFrom).toString());
                }
            }
        };

        handleAuth().catch((error) => {
            console.error("Error:", error);
        });
    }, [user, authStatus, navigate, queryStringFrom, navigateViaRoute, from]);

    const components = {
        SignUp: {
            FormFields() {
                const { validationErrors } = useAuthenticator();
                return (
                    <>
                        <Authenticator.SignUp.FormFields />
                        <Flex gap="0.25rem">
                            <CheckboxField
                                errorMessage={validationErrors.acknowledgement}
                                hasError={!!validationErrors.acknowledgement}
                                name="acknowledgement"
                                value="yes"
                                label="I agree with the Terms of Service"
                            /><a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=b23e7803-82d9-4a25-8575-b1ac84488757" target="_blank"><OpenInNewIcon fontSize="small" sx={{marginTop: "3px", padding: "1px", color: "var(--mui-palette-primary-main)"}}/></a>
                        </Flex>
                    </>
                );
            }
        }
    }

    const services = {
        // async validateCustomSignUp(formData) {
        //     if (!formData.acknowledgement) {
        //         return {
        //             acknowledgement: '',
        //         };
        //     }
        // },
        async handleSignIn(formData) {
            try {
                const { username, password } = formData;
                const signInResult = await Auth.signIn(username, password);
                return signInResult;
            } catch (e) {
                console.error('Sign in error:', e);
                
                // Handle specific error cases
                if (e.message?.includes('Incorrect username or password')) {
                    setError('Incorrect email or password. Please try again. If you created your account using Google, please sign in with Google.');
                } else if (e.message?.includes('User does not exist')) {
                    setError('No account found with this email. Please check your email or sign up for a new account.');
                } else if (e.statusCode === 400 || e.code === 'BadRequestException') {
                    // Handle 400 Bad Request errors from Cognito
                    setError('Authentication failed. Please check your credentials and try again.');
                } else {
                    setError(error.message || 'An error occurred during sign in.');
                }

                throw e;
            }
        },
        // async handleSignUp(formData) {
        //     let { username, password, attributes } = formData;
            
        //     try {
        //         const result = await Auth.signUp({
        //             username,
        //             password,
        //             attributes,
        //             autoSignIn: {
        //                 enabled: true,
        //             },
        //         });
                
        //         await ampli.createAccountButtonClick({"Company": attributes["custom:company"]}); //@@@ TODO: Deprecate in favor of commonCreateAccountCompleted
        //         await ampli.commonCreateAccountCompleted({"Company": attributes["custom:company"]});
        //         await ampli.flush();  // Needed before window.location.href is changed

        //         //@@@ckl DEPRECATED -- signups are superseded by trial subscription activations
        //         // // Track signup with Reddit Pixel using utility function
        //         // trackRedditEvent('SignUp');

        //         // Sign in immediately after successful signup since sign-up option 
        //         // is not reliable:
        //         await Auth.signIn(username, password);
                
        //         // Redirect to subscription page after successful sign-up
        //         navigate("/dashboard");                

        //         return result;
        //     } catch (error) {
        //         if (process.env.NODE_ENV === "development") {
        //             console.error('Error signing up:', error);
        //         }
                
        //         if (error.code == 'UsernameExistsException' || error.message?.includes('UsernameExistsException')) {
        //             setError("An account already exists for that e-mail address.  ");
        //         }

        //         // Let Amplify handle the error naturally
        //         throw error;
        //     }
        // },
    }

    const formFields = {
        signUp: {
            email: {
                placeholder: "Enter your e-mail address",
                isRequired: true,
                label: "E-mail"
            },
            password: {
                placeholder: "Enter your password",
                isRequired: true,
                label: "Password"
            },
            confirm_password: {
                placeholder: "Enter your password again",
                isRequired: true,
                label: "Confirm Password"
            },
            "custom:company": {
                placeholder: "Enter your company name",
                isRequired: false,
                label: "Company"
            }
        }
    }
    
    return (
        // Show the feature card side by wide with our log in       
            <Box key="login-box" sx={{
                display: 'flex',
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                minHeight: 0,   // Prevent overflow if parent is flex
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${nycExposureWebp})`,
                // backgroundSize: '1200px 1200px', // Adjust the dimensions as needed
                // backgroundPosition: 'center bottom',                
                // backgroundImage: `url(${HeptagonsSVG})`,
                
            }}>
                {/* <Box sx={{ 
                    flexShrink: 0,
                    width: { xs: '100%', md: '50%' }, // Changed to percentage for ~400px
                    alignSelf: 'flex-start', // Ensure feature table aligns to top
                    display: { xs: 'none', md: 'block' }, // Hide on mobile
                    pt: '8px' // Added top padding
                }}>  
                    <LaunchPreviewFeatureTable />
                </Box> */}
                <Fade in={true} timeout={1000}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'auto',
                    height: 'auto',
                    minHeight: '400px',
                    minWidth: '500px',                    
                    backgroundColor: 'var(--mui-palette-grey-900)',
                    borderRadius: '14px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    p: 2,
                }}>                                               
                    {error && (
                        <Box sx={{ 
                            backgroundColor: `${red[500]}90`,
                            border: `1px solid ${gray[300]}80`,
                            color: 'white',
                            p: 2,
                            mb: 2,
                            borderRadius: 1,
                            textAlign: 'left',
                            alignSelf: 'center',
                            zIndex: 1,
                            width: '83%',                                
                        }} className="rev-sign-in-error-box">
                            {error}
                        </Box>
                    )}
                    <AmplifyWrapper sx={{ width: '100%', alignSelf: 'flex-start' }}>
                        <div 
                            onClick={() => {
                                const errorBox = document.querySelector('.rev-sign-in-error-box');
                                if (errorBox) {
                                    errorBox.style.transition = 'opacity 0.5s ease-out';
                                    errorBox.style.opacity = '0';
                                    setTimeout(() => setError(''), 500);
                                } else {
                                    setError('');                                        
                                }
                            }}
                        >
                            <Authenticator                                                                    
                                // initialState={tab}
                                //formFields={formFields}
                                loginMechanisms={['email']}
                                // signUpAttributes={['given_name', 'family_name', 'email']}
                                //components={components}
                                services={services}
                                hideSignUp={true}
                                socialProviders={['google']}
                                hideAlert={true}
                            />
                        </div>
                    </AmplifyWrapper>
                </Box>
                </Fade>
                {/* <Box sx={{ 
                    flexShrink: 0,
                    width: { xs: '100%', md: '50%' }, // Changed to percentage for ~400px
                    alignSelf: 'flex-start', // Ensure feature table aligns to top
                    display: { xs: 'flex', md: 'none' }, // Show on mobile
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pt: 5 // Added top padding
                }}>  
                    <LaunchPreviewFeatureTable />
                </Box> */}
            </Box>
    );
}

export default Login; 
